// ============================================================================
// use-batch-jobs-live.ts — one complete change signal for batch_jobs (S1)
// ----------------------------------------------------------------------------
// Why this exists (2026-08-19 incident): the FileExplorer chip, the per-row
// queue locks, and the QueueDrawer each kept themselves fresh off a hand-picked
// list of nine `qm-batch-*` window events. Four real mutation paths emitted
// either nothing (`dequeue`, batch-queue.js) or events on neither list
// (`clearAll` → qm-batch-queue-cleared, `retireProjectJobs` →
// qm-batch-job-cancelled, DLQ dismiss), and the CustomEvent bus is
// window-scoped so a second tab never hears anything. Result: the chip said
// "1 queued", the drawer said "No projects queued", and the row blocked
// "Import now" with "already in the batch queue" — all at once.
//
// The fix is a trigger that CANNOT miss a producer: Dexie `liveQuery` observes
// the batch_jobs table itself, so any write from any code path — including
// paths added later and writes from OTHER TABS (Dexie 4 propagates
// cross-tab natively) — fires the subscriber. Surfaces keep their existing
// refresh logic; only the trigger changes. The qm-batch-* event bus stays for
// non-React listeners; it is no longer load-bearing for queue UI freshness.
//
// Deliberately NOT a data hook: it hands back no rows. Each surface re-reads
// through its own path (listJobs → collapseQueueJobs) so there is exactly one
// derivation of row state (import-workflow.ts) and no second cache to drift.
// ============================================================================
import { useEffect, useRef } from 'react';

type Unsubscribable = { unsubscribe: () => void } | null;

// ── One subscription for the whole app, coalesced (2026-08-20) ─────────────
// Operator: "Queuing in Import Projects lags a lot", equally on cloud and
// local — so client-side, not RTT. Each caller used to open its OWN
// liveQuery(() => batch_jobs.toArray()), and FOUR surfaces subscribe at once
// (the global FileExplorer, the Stage 1 FileExplorer, QueueDrawer and
// WorkspaceProjectTabs). So a single queue write cost four FULL-TABLE reads
// before any consumer had even started its own re-derive — and the enqueue
// path commits more than once per add (position repacking writes a row per
// pending job), multiplying it again.
//
// Two changes, no behaviour lost:
//   1. ONE shared liveQuery, fanned out to every listener.
//   2. Notifications coalesce onto the next frame, so a burst of commits
//      produces ONE refresh per surface instead of one per write.
//
// The trailing edge is preserved BY CONSTRUCTION: the flush always runs after
// the last notification in the burst, and each listener re-reads current state
// itself (this hook hands back no rows — see the header). Losing the final
// state here would resurrect the stale-chip contradiction this file exists to
// prevent, so `_flushQueued` is cleared BEFORE the listeners run: a write that
// lands during a flush schedules another one rather than being swallowed.
const _listeners = new Set<() => void>();
let _sharedSub: Unsubscribable = null;
let _flushQueued = false;

function _runListeners(): void {
    for (const listener of Array.from(_listeners)) {
        try {
            listener();
        } catch (err) {
            // One surface's refresh must not starve the others.
            console.warn('[batch-jobs-live] subscriber refresh threw:', (err as { message?: string })?.message || err);
        }
    }
}

// ── A hidden tab paints no frames (2026-09-21) ────────────────────────────
// requestAnimationFrame does not fire while document.visibilityState is
// 'hidden'. This scheduler used rAF as its ONLY timer, and `_flushQueued`
// latches true until the flush runs, so the first queue write made behind
// another window froze every queue surface — and kept it frozen, because no
// later write could schedule anything either. Measured on the live cloud app
// during a 16-project run: the run sheet showed a job as "Running · Extracting
// files 8m" that had in fact stopped seven minutes earlier, and a remount did
// not correct it.
//
// So a timer that fires in a hidden tab is armed as well. rAF still wins in a
// visible tab, which keeps the frame coalescing this scheduler exists for; the
// timeout is the one that must never be optional. Whichever arrives first
// runs, once.
function _scheduleFlush(): void {
    if (_flushQueued) return;
    _flushQueued = true;
    let ran = false;
    const flush = () => {
        if (ran) return;
        ran = true;
        _flushQueued = false;
        _runListeners();
    };
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(flush);
    setTimeout(flush, 0);
}

// A long-hidden tab has its timers throttled onto a ~1-minute grid, which is
// fine while nobody is looking and wrong the moment they look again. Refresh
// on the way back so what the operator returns to is current, not a snapshot
// of whenever the throttle last let a timer through.
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && _listeners.size > 0) _runListeners();
    });
}

async function _ensureSharedSubscription(): Promise<void> {
    if (_sharedSub || _listeners.size === 0) return;
    const [dexieModule, dbModule] = await Promise.all([
        import('dexie'),
        import('../../services/db'),
    ]);
    // Another caller may have won the race while we awaited, and every
    // listener may have unmounted — re-check both before subscribing.
    if (_sharedSub || _listeners.size === 0) return;
    const liveQuery = (dexieModule as { liveQuery?: (fn: () => unknown) => { subscribe: (o: unknown) => { unsubscribe: () => void } } }).liveQuery;
    const db = (dbModule as { default?: { batch_jobs?: unknown }; db?: { batch_jobs?: unknown } }).default
        || (dbModule as { db?: { batch_jobs?: unknown } }).db;
    if (typeof liveQuery !== 'function' || !(db as { batch_jobs?: unknown })?.batch_jobs) return;
    _sharedSub = liveQuery(() => (db as { batch_jobs: { toArray: () => unknown } }).batch_jobs.toArray()).subscribe({
        next: () => _scheduleFlush(),
        error: (err: { message?: string }) => {
            // A dead subscription silently reintroduces the stale-chip bug — say so.
            console.warn('[batch-jobs-live] subscription error (queue UI may stale until reopen):', err?.message || err);
        },
    });
}

/** Test seam: drop the shared subscription and every listener. */
export function __resetBatchJobsLiveSignalForTests(): void {
    try { _sharedSub?.unsubscribe(); } catch { /* already closed */ }
    _sharedSub = null;
    _listeners.clear();
    _flushQueued = false;
}

/**
 * Invoke `onChange` whenever the Dexie `batch_jobs` table changes, from any
 * producer in any tab. The initial subscription also fires once (Dexie emits
 * the first result immediately); callers' refresh functions are idempotent so
 * this simply guarantees a fresh read at subscribe time.
 *
 * @param enabled subscribe only while the surface is mounted/open
 * @param onChange refresh callback; identity may change freely (kept in a ref)
 */
export function useBatchJobsLiveSignal(enabled: boolean, onChange: () => void): void {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (!enabled) return undefined;
        // Read through the ref so a listener registered once keeps calling the
        // CURRENT callback — identity may change freely, per this hook's contract.
        const listener = () => onChangeRef.current();
        _listeners.add(listener);
        // Dynamic imports match the FileExplorer's existing pattern for the db
        // module (lazy Dexie), and keep this hook dependency-free for unit tests
        // that render without IndexedDB.
        void _ensureSharedSubscription().catch((err: { message?: string }) => {
            console.warn('[batch-jobs-live] failed to subscribe:', err?.message || err);
        });
        // Preserve the fire-once-at-subscribe contract: Dexie emits its first
        // result immediately for a NEW subscription, but a listener joining an
        // EXISTING one would otherwise wait for the next write.
        _scheduleFlush();
        return () => {
            _listeners.delete(listener);
            // Last one out closes the subscription; the next mount reopens it.
            if (_listeners.size === 0) {
                try { _sharedSub?.unsubscribe(); } catch { /* already closed */ }
                _sharedSub = null;
            }
        };
    }, [enabled]);
}
